import { CircularProgress } from "@mui/material";
import MuiAlert, { type AlertProps } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import type React from "react";
import type { ReactNode } from "react";
import { forwardRef } from "react";

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface CustomSnackbarProps {
	open: boolean;
	handleClose: () => void;
	message: string;
	severity: "success" | "info" | "warning" | "error";
	autoHideDuration?: number;
	anchorOrigin?: { vertical: "top" | "bottom"; horizontal: "left" | "center" | "right" };
	offset?: number;
	action?: ReactNode;
	/**
	 * Opt-out of MUI's click-away dismissal (default off, so snackbars still tap-to-dismiss as before).
	 * Needed when the snackbar is opened BY an outside click (e.g. the dropdown min-selection guard) —
	 * that same gesture's trailing `click` would otherwise dismiss it the instant it appears.
	 */
	disableClickawayClose?: boolean;
	/**
	 * A CSS length of chrome pinned to the bottom of the screen (a phone bottom bar) that the toast must clear. The
	 * toast keeps MUI's own gap above it rather than sitting on the viewport edge under the bar. Absent = MUI's own
	 * bottom offset, unchanged.
	 */
	bottomInset?: string;
}

const CustomSnackbar: React.FC<CustomSnackbarProps> = ({
	open,
	handleClose,
	message,
	severity,
	autoHideDuration = 6000,
	anchorOrigin = { vertical: "bottom", horizontal: "left" },
	offset = 0,
	action,
	disableClickawayClose,
	bottomInset,
}) => (
	<Snackbar
		open={open}
		autoHideDuration={autoHideDuration}
		onClose={(_event, reason) => {
			if (disableClickawayClose && reason === "clickaway") return;
			handleClose();
		}}
		anchorOrigin={anchorOrigin}
		sx={(theme) => ({
			"&.MuiSnackbar-root": {
				transform: `translateY(-${offset}px)`,
				// MUI's own gaps (8px on phones, 24px from `sm`), measured from the top of the inset instead of the edge.
				...(bottomInset === undefined
					? {}
					: {
							bottom: `calc(${bottomInset} + ${theme.spacing(1)})`,
							[theme.breakpoints.up("sm")]: { bottom: `calc(${bottomInset} + ${theme.spacing(3)})` },
						}),
			},
		})}
	>
		<Alert
			onClose={handleClose}
			severity={severity}
			sx={{ width: "100%" }}
			icon={severity === "info" && message.includes("Calling") ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : undefined}
			action={action}
		>
			<div data-testid={"system-snackbar"} style={{ whiteSpace: "pre-line" }}>
				{message}
			</div>
		</Alert>
	</Snackbar>
);

export default CustomSnackbar;
